import React, {useRef, useState, forwardRef, useImperativeHandle} from 'react'

import moment from 'moment'

import Languages from '@utils/Languages.js'
import t from 'src/helpers/Languages.js'

import {emit} from 'src/socketio/socketEmit.js'

import {Alert, Button, Colors, IconButton, Modal, P, ScrollView, S2, H4, Card, Row, Spinner, Popup, S1} from '../UI/index.js'
import {
    formSpacing,
    FormRow,
    FormStack,
    Input,
    TimePicker,
    CurrencyInput,
    Recurrence
} from '../UI/forms/index.js'
import DividerHeader from '../UI/surfaces/DividerHeader.js'

import {Activity, Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {User} from '../../../server/functions/users/users.types.js'
import {Customer} from '../../../server/functions/customers/customers.types.js'
import {Shift} from '../../../server/functions/shifts/shifts.types.js'
import {formatDuration} from '@utils/utils.js'
import time from 'server/utils/time.js'
import calcDistance from '@utils/calcDistance.js'
import type {PopupRef} from '../UI/overlays/Popup.js'

type ViewShiftModalProps = {
    reseller: Reseller
    customers: Customer[]
    users: User[]
    onClickEdit: (id: string) => void
    onClickCopy: (id: string) => void
    onClickRemove: (id: string) => Promise<void>
    history: any
    match: any
}

type ViewShiftModalRef = {
    open: (id: string) => void
    close: () => void
}

const ViewShiftModal = forwardRef(({reseller, customers, users, onClickEdit, onClickCopy, onClickRemove, history, match}: ViewShiftModalProps, ref: React.Ref<ViewShiftModalRef>) => {
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [shiftLoading, setShiftLoading] = useState(true)
    const [shift, setShift] = useState<Shift | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [entryPopup, setEntryPopup] = useState<any>(null)

    const popupRef = useRef<PopupRef>(null)

    const open = async (id: string) => {
        setModalIsOpen(true)
        setShiftLoading(true)

        try {
            const {data: shift} = await emit<Shift>('shifts.findById', id)

            console.log(shift)
            setShift(shift)
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setShiftLoading(false)
        }
    }

    const close = () => {
        setModalIsOpen(false)
        setShift(null)
        setShiftLoading(false)
        setEntryPopup(null)

        const {params: {shiftId = ''}} = match
        if (shiftId) {
            history.push('/diensten')
        }
    }

    useImperativeHandle(ref, () => ({
        open,
        close
    }))

    const priceTables = reseller?.settings.shifts?.prices || []
    const customer = customers?.[shift?.customerId]
    const user = users?.[shift?.messengerId]

    const activitiesObject: Record<string, Activity> = {}
    reseller?.settings.shifts?.activities?.map((activity) => {
        activitiesObject[activity.id] = activity
    })

    const shiftActivities = structuredClone(shift?.activities)

    // Add duration for current activity if there is one
    if (shift?.activityId) {
        const currentDuration = time.durationBetween(shift.activityStartTime, time.timestamp())

        shiftActivities[shift.activityId] = shiftActivities[shift.activityId] || {duration: 0}
        shiftActivities[shift.activityId].duration += currentDuration
    }

    // Activities from the settings that aren't in the shift yet, so the planner can add them afterwards
    const addableActivities = (reseller?.settings.shifts?.activities || []).filter((activity) => !shiftActivities?.[activity.id])

    let pausedTime = shift?.pausedTime || 0

    if (shift?.pausedAt) {
        const currentPauseDuration = time.durationBetween(shift?.pausedAt, time.timestamp())

        pausedTime += currentPauseDuration
    }

    return (
        <Modal show={modalIsOpen} onClose={() => close()} style={{position: 'relative'}}>
            <Row style={{alignItems: 'center', marginBottom: 12, position: 'relative'}}>
                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                    <H4>Dienst info</H4>

                    <div style={{flex: 1}} />
                </Row>

                {!shiftLoading &&
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <IconButton
                            tooltip='Bewerk dienst'
                            onClick={() => {
                                close()
                                onClickEdit(shift._id)
                            }}
                            icon='mdi mdi-pencil'
                        />

                        <IconButton
                            tooltip='Kopieer dienst'
                            onClick={() => {
                                close()
                                onClickCopy(shift._id)
                            }}
                            icon='mdi mdi-content-copy'
                        />

                        <IconButton
                            tooltip='Verwijder dienst'
                            onClick={async () => {
                                await onClickRemove(shift._id)
                                close()
                            }}
                            icon='mdi mdi-delete'
                        />
                    </div>
                }

                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                    <div style={{flex: 1}} />

                    <IconButton onClick={close}>
                        <i style={{color: Colors.buttonSolid}} className='mdi mdi-close' />
                    </IconButton>
                </Row>
            </Row>

            {shiftLoading || !shift ?
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250}}>
                    <Spinner color={Colors.textDark} />
                </div> :
                <>
                    <Card style={{marginBottom: 24}}>
                        <Row>
                            <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', minWidth: 'auto'}}>
                                <P icon='mdi mdi-calendar-clock'>{shift.name}</P>
                                <P icon='mdi mdi-calendar'>{`${moment(shift.date).format('DD-MM-YYYY')}`}</P>

                                {shift.rrule && (
                                    <Recurrence
                                        disabled
                                        rrule={shift.rrule}
                                        iconStyle={{color: Colors.textDark, marginRight: 12}}
                                        style={{marginBottom: 0, flex: 'unset', height: 27.5}}
                                    />
                                )}

                                {customer && <P icon='mdi mdi-store-outline'>{customer.name}</P>}

                                {shift.instructions && <P icon='mdi mdi-comment-outline'>{shift.instructions}</P>}

                            </Row>

                            <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, flexDirection: 'column', minWidth: 'auto'}}>

                                <P icon='mdi mdi-clock-outline'>{`${Languages.startTime['NL']}: ${shift.plannedStartTime || '--:--'}`}</P>

                                <P icon='mdi mdi-clock-outline'>{`${Languages.endTime['NL']}: ${shift.plannedEndTime || '--:--'}`}</P>

                                {shift.startPoint &&
                                    <P icon='mdi mdi-map-marker-outline'>{`${Languages.startLocation['NL']}: ${shift.startPoint.street} ${shift.startPoint.nr}${shift.startPoint.addition}, ${shift.startPoint.city}`}</P>
                                }

                                {shift.endPoint &&
                                    <P icon='mdi mdi-map-marker-outline'>{`${Languages.endLocation['NL']}: ${shift.endPoint.street} ${shift.endPoint.nr}${shift.endPoint.addition}, ${shift.endPoint.city}`}</P>
                                }

                            </Row>

                        </Row>
                    </Card>

                    <FormStack style={{marginBottom: formSpacing.section}}>
                        <Input
                            label={Languages.messenger['NL']}
                            value={user?.name}
                            readOnly
                        />

                        <FormRow>
                            <FormRow gap={formSpacing.tight} style={{flex: 1, position: 'relative'}}>
                                <TimePicker
                                    label={Languages.startTime['NL']}
                                    value={shift?.startTime ? moment(shift.startTime).format('HH:mm') : undefined}
                                    readOnly
                                />

                                {shift.startTime &&
                                    <IconButton
                                        icon='mdi mdi-pencil'
                                        onClick={async () => {
                                            popupRef.current.setInputs([
                                                {
                                                    type: 'time',
                                                    name: 'time',
                                                    label: Languages.startTime['NL'],
                                                    value: time.time()
                                                }
                                            ])

                                            const values = await popupRef.current.open('Starttijd aanpassen', '')
                                            if (!values) return
                                            const startTime = moment(`${shift.date} ${values.time}`).toISOString((true))

                                            try {
                                                const {data: updatedShift} = await emit<Shift>('shifts.editStartEndTime', {id: shift._id, startTime})
                                                setShift(updatedShift)
                                                popupRef.current.close()
                                            } catch (err) {
                                                popupRef.current.setError(err.message)
                                            }
                                        }}
                                    />
                                }
                            </FormRow>
                            <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                                <TimePicker
                                    label={Languages.endTime['NL']}
                                    value={shift?.endTime ? moment(shift.endTime).format('HH:mm') : undefined}
                                    readOnly
                                />

                                {shift.endTime &&
                                    <IconButton
                                        icon='mdi mdi-pencil'
                                        onClick={async () => {
                                            popupRef.current.setInputs([
                                                {
                                                    type: 'time',
                                                    name: 'time',
                                                    label: Languages.endTime['NL'],
                                                    value: time.time()
                                                }
                                            ])

                                            const values = await popupRef.current.open('Eindtijd aanpassen', '')
                                            if (!values) return
                                            const endTime = moment(`${shift.date} ${values.time}`).toISOString((true))

                                            try {
                                                const {data: updatedShift} = await emit<Shift>('shifts.editStartEndTime', {id: shift._id, endTime})
                                                setShift(updatedShift)
                                                popupRef.current.close()
                                            } catch (err) {
                                                popupRef.current.setError(err.message)
                                            }
                                        }}
                                    />
                                }
                            </FormRow>
                        </FormRow>

                        <FormRow>
                            <div style={{flex: 1}}>
                                <Button
                                    style={{width: '100%'}}
                                    variant='outline-white'
                                    icon='mdi mdi-flag-checkered'
                                    label={Languages.startShift['NL']}
                                    disabled={shift.started}
                                    onClick={async () => {
                                        popupRef.current.setInputs([
                                            // {
                                            //     type: 'date',
                                            //     name: 'date',
                                            //     label: Languages.date['NL'],
                                            //     value: shift.date

                                            // },
                                            {
                                                type: 'time',
                                                name: 'time',
                                                label: Languages.startTime['NL'],
                                                value: time.time()
                                            }
                                        ])

                                        const values = await popupRef.current.open(Languages.startShift['NL'], '')
                                        if (!values) return
                                        const startTime = moment(`${shift.date} ${values.time}`).toISOString((true))

                                        try {
                                            const {data: updatedShift} = await emit<Shift>('shifts.startShift', {id: shift._id, startTime})
                                            setShift(updatedShift)
                                            popupRef.current.close()
                                        } catch (err) {
                                            popupRef.current.setError(err.message)
                                        }
                                    }}
                                />
                            </div>

                            <div style={{flex: 1}}>
                                {!shift.completed ?
                                    <Button
                                        style={{width: '100%'}}
                                        variant='outline-white'
                                        icon='mdi mdi-flag-checkered'
                                        label={Languages.endShift['NL']}
                                        disabled={!shift.started || shift.completed}
                                        onClick={async () => {
                                            popupRef.current.setInputs([
                                                // {
                                                //     type: 'date',
                                                //     name: 'date',
                                                //     label: Languages.date['NL'],
                                                //     value: shift.date

                                                // },
                                                {
                                                    type: 'time',
                                                    name: 'time',
                                                    label: Languages.endTime['NL'],
                                                    value: time.time()
                                                }
                                            ])

                                            const values = await popupRef.current.open(Languages.endShift['NL'], '')
                                            if (!values) return
                                            const endTime = moment(`${shift.date} ${values.time}`).toISOString((true))

                                            try {
                                                const {data: updatedShift} = await emit<Shift>('shifts.endShift', {id: shift._id, endTime})
                                                setShift(updatedShift)
                                                popupRef.current.close()
                                            } catch (err) {
                                                popupRef.current.setError(err.message)
                                            }
                                        }}
                                    /> :

                                    <Button
                                        style={{width: '100%'}}
                                        variant='outline-white'
                                        icon='mdi mdi-refresh'
                                        label={Languages.reopenShift['NL']}
                                        disabled={!shift.completed}
                                        onClick={async () => {
                                            if (!await popupRef.current.open(Languages.reopenShift['NL'], 'Dienst wordt weer actief, wil je doorgaan?')) return

                                            try {
                                                const {data: updatedShift} = await emit<Shift>('shifts.reopenShift', {id: shift._id})
                                                setShift(updatedShift)
                                                popupRef.current.close()
                                            } catch (err) {
                                                popupRef.current.setError(err.message)
                                            }
                                        }}
                                    />
                                }
                            </div>
                        </FormRow>
                    </FormStack>

                    <DividerHeader style={{marginBottom: 18}} title={Languages.activities['NL']}>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            {Object.entries(shiftActivities).map(([activityId, entry]) => {
                                const activity = activitiesObject[activityId]

                                return (
                                    <div key={activityId} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', columnGap: formSpacing.columnGap}}>

                                        <P
                                            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, background: activity?.color, height: 36, borderRadius: 12}}
                                        >
                                            {activity?.name}
                                        </P>

                                        <TimePicker label={Languages.duration['NL']} duration='seconds' value={entry.duration} readOnly />

                                        {entry.priceHour &&
                                            <CurrencyInput label={Languages.hourlyRate['NL']} value={entry.priceHour || '0,00'} readOnly />
                                        }

                                        {entry.totalPrice &&
                                            <CurrencyInput label={Languages.total['NL']} value={entry.totalPrice || '0,00'} readOnly />
                                        }

                                        <IconButton
                                            icon='mdi mdi-pencil'
                                            disabled={activityId === shift.activityId}
                                            onClick={async () => {
                                                popupRef.current.setInputs([
                                                    {
                                                        type: 'time',
                                                        duration: 'seconds',
                                                        name: 'duration',
                                                        label: Languages.duration['NL'],
                                                        value: entry.duration
                                                    }
                                                ])

                                                const values = await popupRef.current.open(t('editActivityDurationTitle'), '')
                                                if (!values) return

                                                try {
                                                    const {data: updatedShift} = await emit<Shift>('shifts.editActivityDuration', {id: shift._id, activityId, duration: values.duration})
                                                    setShift(updatedShift)
                                                    popupRef.current.close()
                                                } catch (err) {
                                                    popupRef.current.setError(err.message)
                                                }
                                            }}
                                        />
                                    </div>
                                )
                            })}

                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', columnGap: formSpacing.columnGap}}>
                                <P
                                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, background: Colors.grey40, height: 36, borderRadius: 12}}
                                >
                                    {Languages.pause['NL']}
                                </P>

                                <TimePicker label={Languages.duration['NL']} value={formatDuration(pausedTime)} readOnly />

                                <IconButton
                                    icon='mdi mdi-pencil'
                                    disabled={!shift.completed}
                                    onClick={async () => {
                                        popupRef.current.setInputs([
                                            {
                                                type: 'time',
                                                duration: 'seconds',
                                                name: 'duration',
                                                label: Languages.duration['NL'],
                                                value: shift.pausedTime || 0
                                            }
                                        ])

                                        const values = await popupRef.current.open(t('editPauseDurationTitle'), '')
                                        if (!values) return

                                        try {
                                            const {data: updatedShift} = await emit<Shift>('shifts.editPauseDuration', {id: shift._id, duration: values.duration})
                                            setShift(updatedShift)
                                            popupRef.current.close()
                                        } catch (err) {
                                            popupRef.current.setError(err.message)
                                        }
                                    }}
                                />

                            </div>

                            <Button
                                style={{alignSelf: 'flex-start', marginTop: 12}}
                                variant='outline-white'
                                icon='mdi mdi-plus'
                                label={t('addActivity')}
                                disabled={!shift.started || !addableActivities.length}
                                onClick={async () => {
                                    popupRef.current.setInputs([
                                        {
                                            type: 'select',
                                            name: 'activityId',
                                            label: t('activity'),
                                            value: addableActivities[0].id,
                                            options: addableActivities.map((activity) => ({value: activity.id, title: activity.name})),
                                            required: true
                                        },
                                        {
                                            type: 'time',
                                            duration: 'seconds',
                                            name: 'duration',
                                            label: t('duration'),
                                            value: 0
                                        }
                                    ])

                                    const values = await popupRef.current.open(t('addActivity'), '')
                                    if (!values) return

                                    try {
                                        const {data: updatedShift} = await emit<Shift>('shifts.addActivity', {id: shift._id, activityId: values.activityId, duration: values.duration})
                                        setShift(updatedShift)
                                        popupRef.current.close()
                                    } catch (err) {
                                        popupRef.current.setError(err.message)
                                    }
                                }}
                            />
                        </FormStack>
                    </DividerHeader>

                    {reseller.shiftTargetsEnabled && shift.targets?.length > 0 &&
                        <DividerHeader style={{marginBottom: 18}} title={Languages.targets['NL']}>

                            <FormStack style={{marginBottom: formSpacing.section}}>
                                {shift.targets.map((target, index) => {
                                    return (
                                        <div key={`target_${index}`} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', columnGap: formSpacing.columnGap}}>
                                            <P>{Languages[target.type]?.['NL']}</P>

                                            <Input label={Languages.target['NL']} value={target.value} readOnly />

                                            <Input label={Languages.targetExecuted['NL']} value={target.realized || '0'} readOnly />
                                        </div>
                                    )
                                })}
                            </FormStack>
                        </DividerHeader>
                    }

                    <DividerHeader style={{marginBottom: 18}} title={t('administration')}>
                        <FormRow style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                                <Input
                                    label={t('priceTable')}
                                    value={priceTables.find((priceTable) => priceTable.id === shift.priceTableId)?.name || '-'}
                                    readOnly
                                />

                                <IconButton
                                    icon='mdi mdi-pencil'
                                    onClick={async () => {
                                        popupRef.current.setInputs([
                                            {
                                                type: 'select',
                                                name: 'priceTableId',
                                                label: t('priceTable'),
                                                allowEmpty: true,
                                                value: shift.priceTableId || '',
                                                options: priceTables.map((priceTable) => ({value: priceTable.id, title: priceTable.name}))
                                            }
                                        ])

                                        const values = await popupRef.current.open(t('editPriceTable'), '')
                                        if (!values) return

                                        try {
                                            // Recalculates the price and adds the change to the history. Only the price table is sent, the
                                            // shift in this modal can be outdated while the messenger is working.
                                            const {data: updatedShift} = await emit<Shift>('shifts.editAdministration', {id: shift._id, priceTableId: values.priceTableId || ''})
                                            setShift(updatedShift)
                                            popupRef.current.close()
                                        } catch (err) {
                                            popupRef.current.setError(err.message)
                                        }
                                    }}
                                />
                            </FormRow>

                            <FormRow gap={formSpacing.tight} style={{flex: 1}}>
                                <Input
                                    label={t('reference')}
                                    value={shift.reference || '-'}
                                    readOnly
                                />

                                <IconButton
                                    icon='mdi mdi-pencil'
                                    onClick={async () => {
                                        popupRef.current.setInputs([
                                            {
                                                type: 'text',
                                                name: 'reference',
                                                label: t('reference'),
                                                value: shift.reference || ''
                                            }
                                        ])

                                        const values = await popupRef.current.open(t('editReference'), '')
                                        if (!values) return

                                        try {
                                            const {data: updatedShift} = await emit<Shift>('shifts.editAdministration', {id: shift._id, reference: values.reference || ''})
                                            setShift(updatedShift)
                                            popupRef.current.close()
                                        } catch (err) {
                                            popupRef.current.setError(err.message)
                                        }
                                    }}
                                />
                            </FormRow>
                        </FormRow>
                    </DividerHeader>

                    {error && <Alert style={{marginBottom: 6}} variant='danger'>{error}</Alert>}

                    <DividerHeader style={{marginBottom: 18}} title={Languages.history['NL']}>
                        <ScrollView style={{maxHeight: 250}}>
                            {[...shift.history].reverse().map((entry, index) => {
                                const location = entry.location
                                let deviationMeters = 0
                                let deviated = false

                                if (entry.type === 'startShift' && location && shift.startPoint) {
                                    deviationMeters = Math.floor(calcDistance(location, shift.startPoint.position))
                                    deviated = reseller.settings.shifts.deviateStartLocation && deviationMeters >= (reseller.settings.shifts.maxStartEndLocationDeviation || 100)
                                }

                                if (entry.type === 'endShift' && location && shift.endPoint) {
                                    deviationMeters = Math.floor(calcDistance(location, shift.endPoint.position))
                                    deviated = reseller.settings.shifts.deviateEndLocation && deviationMeters >= (reseller.settings.shifts.maxStartEndLocationDeviation || 100)
                                }

                                return (
                                    <div key={`history${index}`} style={{display: 'flex', alignItems: 'center', padding: 5}}>
                                        <div style={{height: 10, width: 10, borderRadius: '50%', marginRight: 15, background: entry.color || Colors.grey20}} />

                                        <div style={{flex: 1}}>
                                            <P>{entry.description }</P>

                                        </div>

                                        <Row style={{paddingLeft: 6, paddingRight: 6, width: 180, marginTop: -10, minWidth: 'auto'}}>
                                            {entry.location &&
                                                <IconButton onClick={() => setEntryPopup({...entry, deviationMeters})} style={{color: !entry.location ? Colors.grey60 : deviated ? Colors.errorBright : Colors.buttonSolid}}>
                                                    <i className='mdi mdi-map-marker-radius' />
                                                </IconButton>
                                            }
                                        </Row>

                                        <S2 style={{marginRight: 15}}>{`${entry.who} | ${moment(entry.timestamp).format('DD-MM-YYYY HH:mm:ss')}`}</S2>
                                    </div>
                                )
                            })}
                        </ScrollView>
                    </DividerHeader>

                    {entryPopup && (
                        <Card style={{position: 'absolute', bottom: 0, left: 812, width: 400, overflowY: 'auto', padding: '12px 12px 24px 12px', background: Colors.backgroundNeutral}}>
                            <Row style={{alignItems: 'center'}}>
                                <H4>{Languages.location['NL']}</H4>

                                <div style={{flex: 1}} />

                                <IconButton onClick={() => setEntryPopup(null)}>
                                    <i style={{color: Colors.buttonSolid}} className='mdi mdi-close' />
                                </IconButton>
                            </Row>

                            <Row style={{marginTop: 24, marginBottom: 24}}>

                                <i className='mdi mdi-circle' style={{marginRight: 6, marginTop: 3, fontSize: 12, color: entryPopup.color || Colors.grey40}} />

                                <Row style={{paddingLeft: 6, paddingRight: 6, flex: 1, minWidth: 'auto'}}>

                                    <P>{entryPopup.description}</P>

                                </Row>

                                <Row style={{paddingLeft: 6, paddingRight: 6, width: 200, minWidth: 'auto'}}>
                                    <S2>{`${entryPopup.who} | ${moment(entryPopup.timestamp).format('DD-MM-YYYY HH:mm:ss')}`}</S2>
                                </Row>

                            </Row>

                            {entryPopup.location ?
                                <>
                                    {entryPopup.deviationMeters > 0 &&
                                        <>
                                            <S1>Afwijking</S1>
                                            <P>{entryPopup.deviationMeters} m ±{parseInt(entryPopup.location.accuracy)} m</P>
                                        </>
                                    }

                                    <br />
                                    <Button
                                        variant='text'
                                        onClick={(event) => {
                                            event.preventDefault()
                                            window.open(`https://www.google.com/maps/search/?api=1&query=${entryPopup.location.lat},${entryPopup.location.lng}`, '_blank')
                                        }}
                                    >
                                        Bekijk op kaart
                                    </Button>
                                </> :
                                <P>Locatie is niet beschikbaar</P>
                            }
                        </Card>
                    )}
                </>
            }

            <Popup ref={popupRef} />
        </Modal>
    )
})

ViewShiftModal.displayName = 'ViewShiftModal'

export default ViewShiftModal
